// Supabase data-access layer for the messaging domain.
// The store (src/store/messages.ts) maps these rows onto the Thread/Message
// shapes the UI already uses, so no screen had to change its rendering.

import { supabase } from '@/integrations/supabase/client';
import type { SharedPostAttachment, Thread, Message, MessageStatus } from '@/types/message';

export const MESSAGE_PAGE_SIZE = 30;

/** Max attachment size (25 MB) and allowed mime types for message media. */
export const MAX_MESSAGE_MEDIA_BYTES = 25 * 1024 * 1024;
export const ALLOWED_MESSAGE_MEDIA_MIME = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'video/mp4',
  'video/quicktime',
  'video/webm',
];

interface ConversationRow {
  id: string;
  type: string;
  created_by: string;
  group_name: string | null;
  context: string | null;
  created_at: string;
  updated_at: string;
}

interface MessageRow {
  id: string;
  conversation_id: string;
  sender_id: string | null;
  content: string;
  media_url: string | null;
  media_type: string | null;
  shared_post: unknown;
  is_system: boolean;
  created_at: string;
  edited_at: string | null;
  deleted_at: string | null;
}

export function mapMessage(row: MessageRow, seenBy: string[], participants: string[]): Message {
  const others = participants.filter((p) => p !== row.sender_id);
  const allSeen = others.length > 0 && others.every((p) => seenBy.includes(p));
  const status: MessageStatus = allSeen ? 'seen' : seenBy.length > 1 ? 'delivered' : 'sent';
  return {
    id: row.id,
    threadId: row.conversation_id,
    senderId: row.sender_id ?? 'system',
    text: row.deleted_at ? '' : row.content,
    createdAt: row.created_at,
    sharedPost: (row.shared_post as SharedPostAttachment | null) ?? undefined,
    mediaUrl: row.media_url ?? undefined,
    mediaType: (row.media_type as Message['mediaType']) ?? undefined,
    editedAt: row.edited_at ?? undefined,
    deletedAt: row.deleted_at ?? undefined,
    status,
    seenBy,
    isSystem: row.is_system,
  };
}

async function readsFor(messageIds: string[]): Promise<Map<string, string[]>> {
  const map = new Map<string, string[]>();
  if (!messageIds.length) return map;
  const { data } = await supabase
    .from('message_reads')
    .select('message_id, user_id')
    .in('message_id', messageIds);
  (data ?? []).forEach((r) => {
    const list = map.get(r.message_id) ?? [];
    list.push(r.user_id);
    map.set(r.message_id, list);
  });
  return map;
}

/** Load every conversation the user belongs to, with its most recent messages. */
export async function fetchThreads(userId: string): Promise<Thread[]> {
  const { data: memberships, error: memberErr } = await supabase
    .from('conversation_members')
    .select('conversation_id')
    .eq('user_id', userId);
  if (memberErr) throw memberErr;

  const convIds = (memberships ?? []).map((m) => m.conversation_id);
  if (!convIds.length) return [];

  const [{ data: convs, error: convErr }, { data: allMembers }, { data: msgs, error: msgErr }] =
    await Promise.all([
      supabase.from('conversations').select('*').in('id', convIds).order('updated_at', { ascending: false }),
      supabase.from('conversation_members').select('conversation_id, user_id').in('conversation_id', convIds),
      supabase
        .from('messages')
        .select('*')
        .in('conversation_id', convIds)
        .order('created_at', { ascending: false })
        .limit(MESSAGE_PAGE_SIZE * Math.min(convIds.length, 20)),
    ]);
  if (convErr) throw convErr;
  if (msgErr) throw msgErr;

  const reads = await readsFor((msgs ?? []).map((m) => m.id));

  const participantsByConv = new Map<string, string[]>();
  (allMembers ?? []).forEach((m) => {
    const list = participantsByConv.get(m.conversation_id) ?? [];
    list.push(m.user_id);
    participantsByConv.set(m.conversation_id, list);
  });

  const messagesByConv = new Map<string, MessageRow[]>();
  (msgs ?? []).forEach((m) => {
    const list = messagesByConv.get(m.conversation_id) ?? [];
    if (list.length < MESSAGE_PAGE_SIZE) list.push(m as MessageRow);
    messagesByConv.set(m.conversation_id, list);
  });

  return (convs ?? []).map((c: ConversationRow) => {
    const participants = participantsByConv.get(c.id) ?? [];
    const rows = (messagesByConv.get(c.id) ?? []).slice().reverse();
    return {
      id: c.id,
      participants,
      messages: rows.map((r) => mapMessage(r, reads.get(r.id) ?? [], participants)),
      context: c.context ?? undefined,
      isGroup: c.type === 'group',
      groupName: c.group_name ?? undefined,
      createdBy: c.created_by,
      createdAt: c.created_at,
    } satisfies Thread;
  });
}

/** Older page of messages for one conversation (progressive loading). */
export async function fetchOlderMessages(
  conversationId: string,
  before: string,
  participants: string[],
): Promise<Message[]> {
  const { data, error } = await supabase
    .from('messages')
    .select('*')
    .eq('conversation_id', conversationId)
    .lt('created_at', before)
    .order('created_at', { ascending: false })
    .limit(MESSAGE_PAGE_SIZE);
  if (error) throw error;
  const rows = (data ?? []) as MessageRow[];
  const reads = await readsFor(rows.map((r) => r.id));
  return rows.slice().reverse().map((r) => mapMessage(r, reads.get(r.id) ?? [], participants));
}

export async function fetchMessageById(id: string, participants: string[]): Promise<Message | null> {
  const { data } = await supabase.from('messages').select('*').eq('id', id).maybeSingle();
  if (!data) return null;
  const reads = await readsFor([id]);
  return mapMessage(data as MessageRow, reads.get(id) ?? [], participants);
}

export async function createConversation(
  createdBy: string,
  participantIds: string[],
  opts: { isGroup?: boolean; context?: string } = {},
): Promise<string> {
  const { data, error } = await supabase
    .from('conversations')
    .insert({
      created_by: createdBy,
      type: opts.isGroup ? 'group' : 'direct',
      context: opts.context ?? null,
    })
    .select('id')
    .single();
  if (error) throw error;
  const conversationId = data.id;
  const members = Array.from(new Set([createdBy, ...participantIds])).map((user_id) => ({
    conversation_id: conversationId,
    user_id,
  }));
  const { error: memberErr } = await supabase.from('conversation_members').insert(members);
  if (memberErr) throw memberErr;
  return conversationId;
}

export async function insertMessage(params: {
  conversationId: string;
  senderId: string | null;
  content: string;
  sharedPost?: SharedPostAttachment;
  mediaUrl?: string;
  mediaType?: string;
  isSystem?: boolean;
}): Promise<string> {
  const { data, error } = await supabase
    .from('messages')
    .insert({
      conversation_id: params.conversationId,
      sender_id: params.senderId,
      content: params.content,
      shared_post: (params.sharedPost as never) ?? null,
      media_url: params.mediaUrl ?? null,
      media_type: params.mediaType ?? null,
      is_system: params.isSystem ?? false,
    })
    .select('id')
    .single();
  if (error) throw error;
  await supabase
    .from('conversations')
    .update({ updated_at: new Date().toISOString() })
    .eq('id', params.conversationId);
  return data.id;
}

export async function markMessagesRead(messageIds: string[], userId: string): Promise<void> {
  if (!messageIds.length) return;
  await supabase
    .from('message_reads')
    .upsert(
      messageIds.map((message_id) => ({ message_id, user_id: userId })),
      { onConflict: 'message_id,user_id', ignoreDuplicates: true },
    );
}

export async function updateConversation(
  conversationId: string,
  patch: { group_name?: string; context?: string },
): Promise<void> {
  const { error } = await supabase.from('conversations').update(patch).eq('id', conversationId);
  if (error) throw error;
}

export async function addMember(conversationId: string, userId: string): Promise<void> {
  const { error } = await supabase
    .from('conversation_members')
    .insert({ conversation_id: conversationId, user_id: userId });
  if (error && error.code !== '23505') throw error;
}

export async function removeMember(conversationId: string, userId: string): Promise<void> {
  const { error } = await supabase
    .from('conversation_members')
    .delete()
    .eq('conversation_id', conversationId)
    .eq('user_id', userId);
  if (error) throw error;
}

export async function editMessage(messageId: string, content: string): Promise<void> {
  const { error } = await supabase
    .from('messages')
    .update({ content, edited_at: new Date().toISOString() })
    .eq('id', messageId);
  if (error) throw error;
}

export async function softDeleteMessage(messageId: string): Promise<void> {
  const { error } = await supabase
    .from('messages')
    .update({ content: '', deleted_at: new Date().toISOString() })
    .eq('id', messageId);
  if (error) throw error;
}
